import { useEffect, useRef, useState } from "react";
import { ChatCircleText, PaperPlaneRight, Sparkle, X } from "@phosphor-icons/react";
import { api } from "../lib/api";

const SUGGESTIONS = [
  "Help me write a strong research query",
  "What are credits and how do I recharge?",
  "How do enterprise connectors work?",
];

const sessionKey = () => {
  let id = localStorage.getItem("ekam_assist_session");
  if (!id) {
    id = `assist_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
    localStorage.setItem("ekam_assist_session", id);
  }
  return id;
};

export const AskEkamWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hi, I am Ekam Guide. Ask me anything about using Ekam — or ask me to help you write a better query for the composer." },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener("ekam:open-assistant", handler);
    return () => window.removeEventListener("ekam:open-assistant", handler);
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const send = async (text) => {
    const message = (text ?? input).trim();
    if (!message || busy) return;
    setInput("");
    setMessages((items) => [...items, { role: "user", content: message }]);
    setBusy(true);
    try {
      const { data } = await api.post("/ekam/assistant/chat", { message, session_id: sessionKey() });
      setMessages((items) => [...items, { role: "assistant", content: data.reply }]);
    } catch (err) {
      setMessages((items) => [...items, { role: "assistant", content: err.response?.data?.detail || "I could not reach the assistant service right now. Please email contact@anxya.tech." }]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button type="button" onClick={() => setOpen(!open)} data-testid="ask-ekam-fab" aria-label="Ask Ekam" className="fixed bottom-24 right-4 z-50 flex h-12 w-12 items-center justify-center rounded-full border border-cyan-400/40 bg-[#0a2527] text-cyan-300 shadow-2xl shadow-cyan-500/20 transition-transform hover:scale-105 sm:bottom-6 sm:right-6">
        {open ? <X size={20} /> : <ChatCircleText size={22} weight="duotone" />}
      </button>
      {open && (
        <div className="fixed bottom-40 right-4 z-50 flex h-[28rem] w-[92vw] max-w-sm flex-col overflow-hidden rounded-3xl border border-cyan-400/25 bg-[#041718] shadow-2xl sm:bottom-24 sm:right-6" data-testid="ask-ekam-panel">
          <div className="flex items-center justify-between border-b border-teal-500/10 bg-[#0a2527] px-5 py-4">
            <div className="flex items-center gap-2">
              <Sparkle size={18} weight="duotone" className="text-cyan-300" />
              <div>
                <p className="text-sm font-semibold text-white">Ask Ekam</p>
                <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Help & query writing</p>
              </div>
            </div>
            <button type="button" onClick={() => setOpen(false)} data-testid="ask-ekam-close" className="rounded-full border border-teal-500/15 p-1.5 text-slate-400 hover:text-white"><X size={14} /></button>
          </div>
          <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto p-4" data-testid="ask-ekam-messages">
            {messages.map((message, index) => (
              <div key={index} data-testid={`ask-ekam-message-${index}`} className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${message.role === "user" ? "ml-auto bg-cyan-400 text-slate-950" : "bg-white/[0.05] text-slate-200"}`}>
                {message.content}
              </div>
            ))}
            {busy && <div className="max-w-[85%] rounded-2xl bg-white/[0.05] px-4 py-3 text-sm text-slate-400" data-testid="ask-ekam-typing">Ekam Guide is thinking…</div>}
          </div>
          {messages.length <= 1 && (
            <div className="flex flex-wrap gap-2 px-4 pb-2">
              {SUGGESTIONS.map((suggestion) => (
                <button key={suggestion} type="button" onClick={() => send(suggestion)} data-testid={`ask-ekam-suggestion-${suggestion.slice(0, 12).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="rounded-full border border-teal-500/15 px-3 py-1.5 text-[11px] text-slate-400 hover:border-cyan-400/40 hover:text-cyan-200">{suggestion}</button>
              ))}
            </div>
          )}
          <div className="flex items-center gap-2 border-t border-teal-500/10 bg-[#0a2527] p-3">
            <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") send(); }} placeholder="Ask for help or a better query…" data-testid="ask-ekam-input" className="h-10 flex-1 rounded-full border border-teal-500/15 bg-[#041718] px-4 text-sm text-white outline-none placeholder:text-slate-600 focus:border-cyan-400/40" />
            <button type="button" onClick={() => send()} disabled={busy || !input.trim()} data-testid="ask-ekam-send" className="flex h-10 w-10 items-center justify-center rounded-full bg-cyan-400 text-slate-950 disabled:opacity-40"><PaperPlaneRight size={16} weight="bold" /></button>
          </div>
        </div>
      )}
    </>
  );
};
